import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import type { IViewPortController } from '@/engine/app/interfaces';
import { SyntheticEvent } from './SyntheticEvent';
import type { PointerSyntheticEvent } from './PointerSyntheticEvent';

export interface IKeyboardHandler {
  onKeyDown: (event: SyntheticEvent<KeyboardEvent>) => void;
  onKeyUp?: (event: SyntheticEvent<KeyboardEvent>) => void;
  onPointerEnter?: (event: PointerSyntheticEvent) => void;
  onPointerLeave?: (event: PointerSyntheticEvent) => void;
  onPointerCancel?: (event: PointerSyntheticEvent) => void;
}

export interface IKeyboardController {
  addKeyboardHandler: (handler: IKeyboardHandler) => void;
}

type _EventHandlerProxy<TEvent extends Event> = (handler: IKeyboardHandler, event: SyntheticEvent<TEvent>) => void;

export class KeyboardController implements IKeyboardController {
  private _viewPortController: IViewPortController;

  private _dependencies: DisposablesBasket = new DisposablesBasket();

  private _handlersStack: Array<IKeyboardHandler> = [];

  public constructor(viewPortController: IViewPortController) {
    this._viewPortController = viewPortController;

    this._dependencies.add(
      this._viewPortController.addKeyboardEventListener('keydown', (event: Event) => {
        this._handleKeyboardEvent(
          (handler: IKeyboardHandler, syntheticEvent: SyntheticEvent<KeyboardEvent>): void => {
            handler.onKeyDown?.(syntheticEvent);
          },
          new SyntheticEvent(event as KeyboardEvent)
        );
      })
    );
    this._dependencies.add(
      this._viewPortController.addKeyboardEventListener('keyup', (event: Event) => {
        this._handleKeyboardEvent(
          (handler: IKeyboardHandler, syntheticEvent: SyntheticEvent<KeyboardEvent>): void => {
            handler.onKeyDown?.(syntheticEvent);
          },
          new SyntheticEvent(event as KeyboardEvent)
        );
      })
    );
    this._dependencies.add(
      this._viewPortController.addEventListener('pointerleave', (event: Event) => {
        this._handleKeyboardEvent(
          (handler: IKeyboardHandler, syntheticEvent: SyntheticEvent<PointerEvent>): void => {
            handler.onPointerLeave?.(syntheticEvent as PointerSyntheticEvent);
          },
          new SyntheticEvent(event as PointerEvent)
        );
      })
    );
    this._dependencies.add(
      this._viewPortController.addEventListener('pointerenter', (event: Event) => {
        this._handleKeyboardEvent(
          (handler: IKeyboardHandler, syntheticEvent: SyntheticEvent<PointerEvent>): void => {
            handler.onPointerEnter?.(syntheticEvent as PointerSyntheticEvent);
          },
          new SyntheticEvent(event as PointerEvent)
        );
      })
    );
    this._dependencies.add(
      this._viewPortController.addEventListener('pointercancel', (event: Event) => {
        this._handleKeyboardEvent(
          (handler: IKeyboardHandler, syntheticEvent: SyntheticEvent<PointerEvent>): void => {
            handler.onPointerCancel?.(syntheticEvent as PointerSyntheticEvent);
          },
          new SyntheticEvent(event as PointerEvent)
        );
      })
    );
  }

  public addKeyboardHandler(handler: IKeyboardHandler): void {
    this._handlersStack.push(handler);
  }

  public removeKeyboardHandler(handler: IKeyboardHandler): void {
    const i = this._handlersStack.indexOf(handler);
    if (i >= 0) {
      this._handlersStack.splice(i, 1);
    }
  }

  public dispose(): void {
    this._dependencies.dispose();
    this._handlersStack = [];
  }

  private _handleKeyboardEvent<TEvent extends Event>(
    handlerProxy: _EventHandlerProxy<TEvent>,
    eventInstance: SyntheticEvent<TEvent>
  ): boolean {
    for (const handler of this._handlersStack) {
      handlerProxy(handler, eventInstance);
      if (eventInstance.isPropagationStopped) {
        break;
      }
    }

    return eventInstance.isPropagationStopped;
  }
}
